/**
 * PortalMonitor - Dashboard for discovering and ingesting documents from the Evercore ISI portal.
 * Shows daemon status (alive/dead), cookie health, scan history, and document management.
 */

import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Tooltip,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CloudDownload as IngestIcon,
  Search as SearchIcon,
  Block as SkipIcon,
  FiberManualRecord as DotIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import {
  monitorService,
  PortalDocument,
  MonitorStats,
  DaemonStatus,
  ScanHistoryEntry,
} from '../services/monitorService';

const statusColors: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning' | 'info'> = {
  new: 'info',
  queued: 'warning',
  ingesting: 'primary',
  ingested: 'success',
  failed: 'error',
  skipped: 'default',
};

const sectionLabel: Record<string, string> = {
  fundamental: 'Fundamental',
  macro: 'Macro',
  media: 'Media',
};

function formatDate(iso: string | null): string {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
}

function timeAgo(iso: string | null): string {
  if (!iso) return 'never';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function timeUntil(iso: string | null): string {
  if (!iso) return '-';
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return 'any moment';
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `~${mins}m`;
  return `~${Math.floor(mins / 60)}h`;
}

function CookieChip({ status }: { status: string }) {
  const map: Record<string, { color: 'success' | 'error' | 'warning' | 'default'; label: string }> = {
    ok: { color: 'success', label: 'OK' },
    expired: { color: 'error', label: 'Expired' },
    missing: { color: 'error', label: 'Missing' },
    unknown: { color: 'warning', label: 'Unknown' },
  };
  const cfg = map[status] || map.unknown;
  return <Chip label={`Cookies: ${cfg.label}`} color={cfg.color} size="small" variant="outlined" />;
}

export default function PortalMonitor() {
  const [documents, setDocuments] = useState<PortalDocument[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [stats, setStats] = useState<MonitorStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [ingesting, setIngesting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' }>({
    open: false, message: '', severity: 'info',
  });

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [sectionFilter, setSectionFilter] = useState('all');
  const [searchText, setSearchText] = useState('');

  // Daemon
  const [daemon, setDaemon] = useState<DaemonStatus | null>(null);
  const [scanHistory, setScanHistory] = useState<ScanHistoryEntry[]>([]);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchDocuments = useCallback(async () => {
    try {
      setLoading(true);
      const res = await monitorService.getDocuments({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        section: sectionFilter !== 'all' ? sectionFilter : undefined,
        search: searchText || undefined,
        limit: 100,
      });
      setDocuments(res.documents);
      setError(null);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter, sectionFilter, searchText]);

  const fetchStats = useCallback(async () => {
    try {
      const s = await monitorService.getStats();
      setStats(s);
    } catch {
      // stats are non-critical
    }
  }, []);

  const fetchDaemonStatus = useCallback(async () => {
    try {
      const s = await monitorService.getDaemonStatus();
      setDaemon(s);
    } catch {
      // non-critical
    }
  }, []);

  const fetchScanHistory = useCallback(async () => {
    try {
      const res = await monitorService.getScanHistory(20);
      setScanHistory(res.scans || []);
    } catch {
      // non-critical
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchDocuments();
    fetchStats();
    fetchDaemonStatus();
    fetchScanHistory();
  }, [fetchDocuments, fetchStats, fetchDaemonStatus, fetchScanHistory]);

  // Poll every 30s
  useEffect(() => {
    pollRef.current = setInterval(() => {
      fetchDaemonStatus();
      fetchStats();
      fetchScanHistory();
    }, 30000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchDaemonStatus, fetchStats, fetchScanHistory]);

  // Toggle selection
  const toggleSelect = (id: string) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === documents.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(documents.map(d => d.id)));
    }
  };

  // Manual scan
  const handleScan = async () => {
    setScanning(true);
    try {
      const result = await monitorService.scanPortal();
      setSnackbar({ open: true, message: `Scan complete: ${result.new} new, ${result.existing} existing`, severity: 'success' });
      fetchDocuments();
      fetchStats();
      fetchDaemonStatus();
      fetchScanHistory();
    } catch (e: any) {
      setSnackbar({ open: true, message: `Scan failed: ${e.message}`, severity: 'error' });
    } finally {
      setScanning(false);
    }
  };

  // Daemon start/stop
  const handleToggleDaemon = async () => {
    try {
      if (daemon?.status === 'running') {
        await monitorService.stopAutoScan();
        setSnackbar({ open: true, message: 'Daemon stop signal sent', severity: 'info' });
      } else {
        await monitorService.startAutoScan();
        setSnackbar({ open: true, message: 'Daemon start signal sent', severity: 'success' });
      }
      setTimeout(fetchDaemonStatus, 1000);
    } catch (e: any) {
      setSnackbar({ open: true, message: `Daemon toggle failed: ${e.message}`, severity: 'error' });
    }
  };

  // Config changes
  const handleIntervalChange = async (value: number) => {
    try {
      await monitorService.updateDaemonConfig({ interval_minutes: value });
      setSnackbar({ open: true, message: `Interval updated to ${value} min`, severity: 'success' });
      fetchDaemonStatus();
    } catch (e: any) {
      setSnackbar({ open: true, message: `Config update failed: ${e.message}`, severity: 'error' });
    }
  };

  const handleAutoIngestToggle = async () => {
    try {
      const newVal = !daemon?.auto_ingest;
      await monitorService.updateDaemonConfig({ auto_ingest: newVal });
      setSnackbar({ open: true, message: `Auto-ingest ${newVal ? 'enabled' : 'disabled'}`, severity: 'success' });
      fetchDaemonStatus();
    } catch (e: any) {
      setSnackbar({ open: true, message: `Config update failed: ${e.message}`, severity: 'error' });
    }
  };

  const handleNotificationsToggle = async () => {
    try {
      const newVal = !daemon?.notifications_enabled;
      if (newVal) {
        await monitorService.enableNotifications();
      } else {
        await monitorService.disableNotifications();
      }
      setSnackbar({ open: true, message: `Notifications ${newVal ? 'enabled' : 'disabled'}`, severity: 'success' });
      fetchDaemonStatus();
    } catch (e: any) {
      setSnackbar({ open: true, message: `Notification toggle failed: ${e.message}`, severity: 'error' });
    }
  };

  const handleChannelChange = async (_: React.MouseEvent<HTMLElement>, newChannel: string | null) => {
    if (!newChannel) return; // Don't allow deselecting all
    try {
      await monitorService.updateConfig({ notification_channel: newChannel });
      setSnackbar({ open: true, message: `Channel set to ${newChannel}`, severity: 'success' });
      fetchDaemonStatus();
    } catch (e: any) {
      setSnackbar({ open: true, message: `Channel update failed: ${e.message}`, severity: 'error' });
    }
  };

  // Ingest selected
  const handleIngest = async () => {
    if (selected.size === 0) return;
    setIngesting(true);
    try {
      const result = await monitorService.ingestDocuments(Array.from(selected));
      setSnackbar({
        open: true,
        message: `Queued ${result.queued} document(s) for ingestion${result.errors.length ? `. Errors: ${result.errors.join(', ')}` : ''}`,
        severity: result.errors.length ? 'warning' as any : 'success',
      });
      setSelected(new Set());
      fetchDocuments();
      fetchStats();
    } catch (e: any) {
      setSnackbar({ open: true, message: `Ingest failed: ${e.message}`, severity: 'error' });
    } finally {
      setIngesting(false);
    }
  };

  // Skip selected
  const handleSkip = async () => {
    if (selected.size === 0) return;
    try {
      const ids = Array.from(selected);
      for (let i = 0; i < ids.length; i++) {
        await monitorService.updateDocument(ids[i], { status: 'skipped' });
      }
      setSnackbar({ open: true, message: `Skipped ${selected.size} document(s)`, severity: 'info' });
      setSelected(new Set());
      fetchDocuments();
      fetchStats();
    } catch (e: any) {
      setSnackbar({ open: true, message: `Skip failed: ${e.message}`, severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>Portal Monitor</Typography>
        <Button
          variant="outlined"
          startIcon={scanning ? <CircularProgress size={18} /> : <RefreshIcon />}
          onClick={handleScan}
          disabled={scanning}
          size="small"
        >
          {scanning ? 'Scanning...' : 'Scan Now'}
        </Button>
      </Box>

      {/* Daemon Status Panel */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 3, alignItems: 'center', flexWrap: 'wrap' }}>
          {/* Alive/Dead */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" fontWeight={600}>Daemon:</Typography>
            <Chip
              icon={<DotIcon sx={{ fontSize: 12 }} />}
              label={daemon?.is_alive ? 'Alive' : 'Dead'}
              color={daemon?.is_alive ? 'success' : 'error'}
              size="small"
              variant="filled"
            />
          </Box>

          {/* Cookie status */}
          <CookieChip status={daemon?.cookie_status || 'unknown'} />

          {/* Last scan */}
          <Typography variant="body2" color="text.secondary">
            Last scan: {timeAgo(daemon?.last_scan_at || null)}
            {daemon?.last_scan_new != null && daemon.last_scan_new > 0 && (
              <> ({daemon.last_scan_new} new)</>
            )}
          </Typography>

          {/* Next scan ETA */}
          {daemon?.is_alive && daemon?.next_scan_eta && (
            <Typography variant="body2" color="text.secondary">
              Next: {timeUntil(daemon.next_scan_eta)}
            </Typography>
          )}

          {/* Total scans */}
          {daemon && daemon.scan_count > 0 && (
            <Typography variant="body2" color="text.secondary">
              Scans: {daemon.scan_count}
            </Typography>
          )}

          {/* Last error */}
          {daemon?.last_error && (
            <Tooltip title={`${daemon.last_error} (${timeAgo(daemon.last_error_at)})`} arrow>
              <Chip label="Error" color="error" size="small" variant="outlined" />
            </Tooltip>
          )}
        </Box>

        {/* Config row */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 1.5, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" fontWeight={600}>Status:</Typography>
            <Switch
              checked={daemon?.status === 'running'}
              onChange={handleToggleDaemon}
              color="primary"
              size="small"
            />
            <Typography variant="caption" color="text.secondary">
              {daemon?.status === 'running' ? 'Running' : 'Stopped'}
            </Typography>
          </Box>

          <FormControl size="small" sx={{ minWidth: 100 }}>
            <InputLabel>Interval</InputLabel>
            <Select
              value={daemon?.interval_minutes || 30}
              label="Interval"
              onChange={e => handleIntervalChange(Number(e.target.value))}
            >
              <MenuItem value={5}>5 min</MenuItem>
              <MenuItem value={15}>15 min</MenuItem>
              <MenuItem value={30}>30 min</MenuItem>
              <MenuItem value={60}>1 hour</MenuItem>
              <MenuItem value={120}>2 hours</MenuItem>
            </Select>
          </FormControl>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2">Auto-Ingest:</Typography>
            <Switch
              checked={daemon?.auto_ingest || false}
              onChange={handleAutoIngestToggle}
              color="primary"
              size="small"
            />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2">Notifications:</Typography>
            <Switch
              checked={daemon?.notifications_enabled ?? true}
              onChange={handleNotificationsToggle}
              color="success"
              size="small"
            />
            <Typography variant="caption" color={daemon?.notifications_enabled === false ? 'error' : 'text.secondary'}>
              {daemon?.notifications_enabled === false ? 'Off' : 'On'}
            </Typography>
          </Box>

          {daemon?.notifications_enabled !== false && (
            <ToggleButtonGroup
              value={daemon?.notification_channel ?? 'both'}
              exclusive
              onChange={handleChannelChange}
              size="small"
              sx={{ height: 28 }}
            >
              <ToggleButton value="whatsapp" sx={{ textTransform: 'none', px: 1.5, fontSize: '0.75rem' }}>
                WhatsApp
              </ToggleButton>
              <ToggleButton value="telegram" sx={{ textTransform: 'none', px: 1.5, fontSize: '0.75rem' }}>
                Telegram
              </ToggleButton>
              <ToggleButton value="both" sx={{ textTransform: 'none', px: 1.5, fontSize: '0.75rem' }}>
                Both
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          {daemon?.pid && (
            <Typography variant="caption" color="text.secondary">
              PID: {daemon.pid}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* No cookies warning */}
      {daemon && daemon.cookie_status === 'expired' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Portal cookies have expired. Update cookies.json and restart the daemon.
        </Alert>
      )}
      {daemon && daemon.cookie_status === 'missing' && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No cookies.json found on the server. Place a valid cookies.json in the project root (same format used by scraper_v3.py).
        </Alert>
      )}

      {/* Stats bar */}
      {stats && (
        <Paper sx={{ p: 2, mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Typography variant="body2" fontWeight={600} sx={{ mr: 1 }}>Status:</Typography>
          <Chip label={`${stats.new} new`} color="info" size="small" />
          <Chip label={`${stats.queued} queued`} color="warning" size="small" />
          <Chip label={`${stats.ingesting} ingesting`} color="primary" size="small" />
          <Chip label={`${stats.ingested} ingested`} color="success" size="small" />
          <Chip label={`${stats.failed} failed`} color="error" size="small" />
          <Chip label={`${stats.skipped} skipped`} size="small" />
          <Box sx={{ flexGrow: 1 }} />
          <Typography variant="caption" color="text.secondary">
            Sections: {Object.entries(stats.by_section).map(([k, v]) => `${sectionLabel[k] || k}: ${v}`).join(' | ')}
          </Typography>
        </Paper>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={e => setStatusFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="new">New</MenuItem>
            <MenuItem value="queued">Queued</MenuItem>
            <MenuItem value="ingesting">Ingesting</MenuItem>
            <MenuItem value="ingested">Ingested</MenuItem>
            <MenuItem value="failed">Failed</MenuItem>
            <MenuItem value="skipped">Skipped</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 130 }}>
          <InputLabel>Section</InputLabel>
          <Select value={sectionFilter} label="Section" onChange={e => setSectionFilter(e.target.value)}>
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="fundamental">Fundamental</MenuItem>
            <MenuItem value="macro">Macro</MenuItem>
            <MenuItem value="media">Media</MenuItem>
          </Select>
        </FormControl>

        <TextField
          size="small"
          placeholder="Search title..."
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} /> }}
          sx={{ minWidth: 220 }}
        />

        <Box sx={{ flexGrow: 1 }} />

        {selected.size > 0 && (
          <>
            <Button
              variant="contained"
              color="primary"
              startIcon={ingesting ? <CircularProgress size={18} color="inherit" /> : <IngestIcon />}
              onClick={handleIngest}
              disabled={ingesting}
            >
              Ingest Selected ({selected.size})
            </Button>
            <Button
              variant="outlined"
              startIcon={<SkipIcon />}
              onClick={handleSkip}
            >
              Skip Selected
            </Button>
          </>
        )}
      </Paper>

      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Documents table */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={documents.length > 0 && selected.size === documents.length}
                  indeterminate={selected.size > 0 && selected.size < documents.length}
                  onChange={toggleSelectAll}
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Title</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Section</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Ticker</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Analyst</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Sector</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Pages</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Portal Date</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                  <Typography color="text.secondary">
                    {daemon?.is_alive
                      ? 'No documents yet. Daemon is running and will populate this table automatically.'
                      : 'No documents found. Click "Scan Now" or start the daemon to discover portal content.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              documents.map(doc => (
                <TableRow
                  key={doc.id}
                  hover
                  selected={selected.has(doc.id)}
                  sx={{ cursor: 'pointer' }}
                  onClick={() => toggleSelect(doc.id)}
                >
                  <TableCell padding="checkbox">
                    <Checkbox checked={selected.has(doc.id)} />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Tooltip title={doc.abstract || doc.title} arrow>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 370 }}>
                          {doc.title}
                        </Typography>
                      </Tooltip>
                      {doc.portal_url && (
                        <Tooltip title={doc.portal_url} arrow>
                          <a
                            href={doc.portal_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={e => e.stopPropagation()}
                            style={{ display: 'flex', alignItems: 'center' }}
                          >
                            <OpenInNewIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          </a>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={sectionLabel[doc.section] || doc.section}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={doc.ticker ? 600 : 400}>
                      {doc.ticker || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{doc.analyst || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{doc.sector || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{doc.pages || '-'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatDate(doc.portal_date)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={doc.status}
                      color={statusColors[doc.status] || 'default'}
                      size="small"
                    />
                    {doc.error_message && (
                      <Tooltip title={doc.error_message} arrow>
                        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                          {doc.error_message.slice(0, 40)}...
                        </Typography>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Scan History */}
      {scanHistory.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>Scan History</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">New</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Existing</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Duration</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {scanHistory.map(scan => (
                  <TableRow key={scan.id}>
                    <TableCell>
                      <Tooltip title={scan.scanned_at} arrow>
                        <Typography variant="body2">{timeAgo(scan.scanned_at)}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color={scan.new_count > 0 ? 'primary' : 'text.secondary'}>
                        {scan.new_count}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">{scan.existing_count}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" color="text.secondary">
                        {scan.duration_sec != null ? `${scan.duration_sec.toFixed(1)}s` : '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {scan.error ? (
                        <Tooltip title={scan.error} arrow>
                          <Typography variant="body2" color="error" noWrap sx={{ maxWidth: 200 }}>
                            {scan.error.slice(0, 50)}...
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
